import { isUnknownArray } from "../types/data";
import { getCountryCode } from "./countryNames";

const asArray = <T>(value: T | T[] | null | undefined): T[] => isUnknownArray(value) ? value : value ? [value] : [];

const normalizeGroupKey = (value: unknown) => {
  const key = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

  const aliases: Record<string, string> = {
    europe: "europe",
    европа: "europe",
    asia: "asia",
    азия: "asia",
    africa: "africa",
    африка: "africa",
    northamerica: "northAmerica",
    севернаяамерика: "northAmerica",
    southamerica: "southAmerica",
    южнаяамерика: "southAmerica",
    oceania: "oceania",
    australia: "oceania",
    океания: "oceania",
    австралия: "oceania",
    antarctica: "antarctica",
    антарктида: "antarctica"
  };
  return aliases[key] || key;
};

export const COUNTRY_GROUP_CODES: Readonly<Record<string, readonly string[]>> = Object.freeze({
  europe: [
    "AD", "AL", "AT", "AX", "BA", "BE", "BG", "BY", "CH", "CY", "CZ", "DE", "DK",
    "EE", "ES", "FI", "FO", "FR", "GB", "GG", "GI", "GR", "HR", "HU", "IE", "IM",
    "IS", "IT", "JE", "LI", "LT", "LU", "LV", "MC", "MD", "ME", "MK", "MT", "NL",
    "NO", "PL", "PT", "RO", "RS", "RU", "SE", "SI", "SJ", "SK", "SM", "SU", "UA",
    "VA", "XK"
  ],
  asia: [
    "AE", "AF", "AM", "AZ", "BD", "BH", "BN", "BT", "CC", "CN", "CX", "CY", "GE",
    "HK", "ID", "IL", "IN", "IO", "IQ", "IR", "JO", "JP", "KG", "KH", "KP", "KR",
    "KW", "KZ", "LA", "LB", "LK", "MM", "MN", "MO", "MV", "MY", "NP", "OM", "PH",
    "PK", "PS", "QA", "RU", "SA", "SG", "SU", "SY", "TH", "TJ", "TL", "TM", "TR",
    "TW", "UZ", "VN", "YE"
  ],
  africa: [
    "AO", "BF", "BI", "BJ", "BW", "CD", "CF", "CG", "CI", "CM", "CV", "DJ", "DZ",
    "EG", "EH", "ER", "ET", "GA", "GH", "GM", "GN", "GQ", "GW", "KE", "KM", "LR",
    "LS", "LY", "MA", "MG", "ML", "MR", "MU", "MW", "MZ", "NA", "NE", "NG", "RE",
    "RW", "SC", "SD", "SH", "SL", "SN", "SO", "SS", "ST", "SZ", "TD", "TG", "TN",
    "TZ", "UG", "YT", "ZA", "ZM", "ZW"
  ],
  northAmerica: [
    "AG", "AI", "AW", "BB", "BL", "BM", "BQ", "BS", "BZ", "CA", "CR", "CU", "CW",
    "DM", "DO", "GD", "GL", "GP", "GT", "HN", "HT", "JM", "KN", "KY", "LC", "MF",
    "MQ", "MS", "MX", "NI", "PA", "PM", "PR", "SV", "SX", "TC", "TT", "US", "VC",
    "VG", "VI"
  ],
  southAmerica: [
    "AR", "BO", "BR", "CL", "CO", "EC", "FK", "GF", "GY", "PE", "PY", "SR", "UY", "VE"
  ],
  oceania: [
    "AS", "AU", "CK", "FJ", "FM", "GU", "HM", "KI", "MH", "MP", "NC", "NF", "NR",
    "NU", "NZ", "PF", "PG", "PN", "PW", "SB", "TK", "TO", "TV", "UM", "VU", "WF", "WS"
  ],
  antarctica: ["AQ", "BV", "GS", "HM", "TF"]
});

const COUNTRY_GROUP_CODE_SETS = Object.freeze(
  Object.fromEntries(
    Object.entries(COUNTRY_GROUP_CODES).map(([key, codes]) => [key, new Set(codes)])
  )
);

export const CHALLENGE_CONTINENT_GROUP_KEYS = [
  "europe",
  "asia",
  "africa",
  "northAmerica",
  "southAmerica",
  "oceania"
];

export const getCountryGroupCodes = (groupKey: string) =>
  COUNTRY_GROUP_CODES[normalizeGroupKey(groupKey)] || [];

export const countryCodeBelongsToGroup = (countryCode: unknown, groupKey: string) => {
  const code = String(countryCode || "").trim().toUpperCase();
  if (!code) return false;
  return COUNTRY_GROUP_CODE_SETS[normalizeGroupKey(groupKey)]?.has(code) || false;
};

export const countryBelongsToGroup = (country: unknown, groupKey: string) => {
  const code = getCountryCode(country);
  return Boolean(code && countryCodeBelongsToGroup(code, groupKey));
};

export const getCountryCodes = (countries: unknown = []) => {
  const seen = new Set<string>();
  return asArray(countries)
    .flatMap(asArray)
    .map(getCountryCode)
    .filter(Boolean)
    .filter((code) => {
      if (seen.has(code)) return false;
      seen.add(code);
      return true;
    });
};
